import {
  createFileRoute, redirect,
} from '@tanstack/react-router';
import AdditionalTradeReferences from '../../../components/pages/additional-trade-references';
import {
  ApiError,
  isAdditionalTradeReferencesNotFound,
  isAdditionalTradeReferencesUnauthorized,
  isNotFoundError,
  isUnauthorizedError,
  NotFoundErrorCode,
} from '../../../utils/errors';
import { getSettingsByUrlSlug } from '../../../api/settings/get-by-url-slug';
import { SurveySettings } from '../../../types';
import { getAdditionalTradeReferencesData } from '../../../api/submissions/get-additional-trade-references';

export interface AdditionalTradeReferencesSearchParams {
  token?: string;
}

interface AdditionalTradeReferencesLoaderParams {
  params: { accountSlug: string; submissionId: string };
  deps: AdditionalTradeReferencesSearchParams;
}

export interface AdditionalTradeReferencesLoaderResponse {
  settings: SurveySettings;
  error?: ApiError;
}

async function additionalTradeReferencesLoader ({
  params: { accountSlug, submissionId },
  deps: { token },
}: AdditionalTradeReferencesLoaderParams): Promise<AdditionalTradeReferencesLoaderResponse> {
  try {
    const additionalTradeReferencesData = await getAdditionalTradeReferencesData(accountSlug, submissionId, token);

    return {
      settings: additionalTradeReferencesData.settings,
    };
  } catch (error) {
    if (isNotFoundError(error) && error.code === NotFoundErrorCode.SettingsNotFound) {
      throw redirect({ to: '/' });
    }

    if (isAdditionalTradeReferencesNotFound(error) ||
      isAdditionalTradeReferencesUnauthorized(error) ||
      isUnauthorizedError(error) ||
      (isNotFoundError(error) && error.code === NotFoundErrorCode.SubmissionNotFound)) {
      const settings = await getSettingsByUrlSlug(accountSlug);
      return {
        settings,
        error,
      };
    }

    throw error;
  }
}

/**
 * Returns route configuration options for additional trade references routes
 * Can be spread directly in the route configuration
 */
function additionalTradeReferencesRouteLoader () {
  return {
    // Extract loader dependencies - fallback to search for backward compat
    loaderDeps: ({ search }: { search: AdditionalTradeReferencesSearchParams }): AdditionalTradeReferencesSearchParams => ({
      token: search.token,
    }),

    // Load data using the validated params
    loader: async ({ params, deps }: AdditionalTradeReferencesLoaderParams) =>
      additionalTradeReferencesLoader({
        params,
        deps,
      }),
  };
}

export const Route = createFileRoute('/$accountSlug/$submissionId/additional-trade-references')({
  ...additionalTradeReferencesRouteLoader(),
  component: AdditionalTradeReferences,
});
